import { useState, useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import { X, Users, Plus, Hash, Loader, RefreshCw } from 'lucide-react'
import { useStore } from '../store/useStore'
import { createRoom } from '../hooks/usePartyKit'

const PRESET_TAGS = [
  { value: 'beginner', label: 'Beginner', color: 'bg-green-500/20 text-green-400' },
  { value: 'intermediate', label: 'Intermediate', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'advanced', label: 'Advanced', color: 'bg-red-500/20 text-red-400' },
  { value: 'generative', label: 'Generative Art', color: 'bg-purple-500/20 text-purple-400' },
  { value: 'game', label: 'Game Dev', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'tutorial', label: 'Tutorial', color: 'bg-cyan-500/20 text-cyan-400' },
  { value: 'dyslexia-friendly', label: 'Dyslexia Friendly', color: 'bg-pink-500/20 text-pink-400' },
  { value: 'shader', label: 'Shaders', color: 'bg-orange-500/20 text-orange-400' },
]

export default function SocialRoomPicker({ onClose }) {
  const { user } = useUser()
  const { joinRoom } = useStore()
  
  const [mode, setMode] = useState('browse') // 'browse' or 'create'
  const [rooms, setRooms] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [filterTags, setFilterTags] = useState([])
  
  // Create room form
  const [roomName, setRoomName] = useState('')
  const [selectedTags, setSelectedTags] = useState([])
  
  // Fetch active rooms
  useEffect(() => {
    fetchRooms()
  }, [])
  
  const fetchRooms = async () => {
    setLoading(true)
    try {
      // In production, this would fetch from your API that tracks active rooms
      const response = await fetch('/api/rooms')
      if (response.ok) {
        const data = await response.json()
        setRooms(data.rooms || [])
      }
    } catch (e) {
      console.error('[SocialRoomPicker] Error fetching rooms:', e)
      // Mock data for development
      setRooms([
        { id: 'demo-room-1', name: 'Particle Fun', tags: ['beginner', 'generative'], userCount: 3 },
        { id: 'demo-room-2', name: 'Shader Lab', tags: ['advanced', 'shader'], userCount: 2 },
        { id: 'demo-room-3', name: 'Learn Together', tags: ['tutorial', 'dyslexia-friendly'], userCount: 5 },
      ])
    } finally {
      setLoading(false)
    }
  }
  
  const handleCreateRoom = async () => {
    if (!roomName.trim() || !user) return
    
    setCreating(true)
    try {
      const roomId = `room-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      
      // Initialize room in PartyKit
      await createRoom(roomId, roomName.trim(), selectedTags, user.id)
      
      // Also register in D1 so it shows up in browse list
      const registerResponse = await fetch('/api/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: roomId,
          name: roomName.trim(),
          tags: selectedTags,
          createdBy: user.id
        })
      })
      if (!registerResponse.ok) {
        throw new Error('Failed to register room in database')
      }
      
      // Join the room we just created
      joinRoom(roomId, roomName.trim())
      onClose()
    } catch (e) {
      console.error('[SocialRoomPicker] Error creating room:', e)
    } finally {
      setCreating(false)
    }
  }
  
  const handleJoinRoom = async (room) => {
    // Notify D1 API to increment user count
    try {
      await fetch('/api/rooms', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId: room.id, action: 'join' })
      })
    } catch (e) {
      console.error('[SocialRoomPicker] Failed to notify room join:', e)
    }
    joinRoom(room.id, room.name)
    onClose()
  }
  
  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    )
  }
  
  const toggleFilterTag = (tag) => {
    setFilterTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    )
  }
  
  const filteredRooms = filterTags.length > 0
    ? rooms.filter(room => room.tags?.some(tag => filterTags.includes(tag)))
    : rooms
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-ide-surface border border-ide-border rounded-xl max-w-xl w-full max-h-[80vh] flex flex-col shadow-modal animate-scale-in">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-ide-border">
          <div className="flex items-center gap-2">
            <Users className="text-ide-accent" size={20} />
            <h2 className="text-lg font-semibold text-ide-text">Social Coding</h2>
          </div>
          <button
            onClick={onClose}
            className="text-ide-textMuted hover:text-ide-text transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-ide-border">
          <button
            onClick={() => setMode('browse')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              mode === 'browse' 
                ? 'text-ide-accent border-b-2 border-ide-accent' 
                : 'text-ide-textMuted hover:text-ide-text'
            }`}
          >
            Browse Rooms
          </button>
          <button
            onClick={() => setMode('create')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              mode === 'create' 
                ? 'text-ide-accent border-b-2 border-ide-accent' 
                : 'text-ide-textMuted hover:text-ide-text'
            }`}
          >
            Create Room
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {mode === 'browse' ? (
            <div className="space-y-4">
              {/* Tag Filters */}
              <div>
                <p className="text-xs text-ide-textMuted mb-2">Filter by tags:</p>
                <div className="flex flex-wrap gap-1.5">
                  {PRESET_TAGS.map(tag => (
                    <button
                      key={tag.value}
                      onClick={() => toggleFilterTag(tag.value)}
                      className={`px-2 py-1 rounded text-xs transition-all ${
                        filterTags.includes(tag.value)
                          ? tag.color + ' ring-1 ring-current'
                          : 'bg-ide-bg text-ide-textMuted hover:text-ide-text'
                      }`}
                    >
                      #{tag.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Room List */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-ide-textMuted">
                    {filteredRooms.length} active room{filteredRooms.length !== 1 ? 's' : ''}
                  </p>
                  <button
                    onClick={fetchRooms}
                    disabled={loading}
                    className="text-ide-textMuted hover:text-ide-text transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                  </button>
                </div>
                
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="animate-spin text-ide-textMuted" size={24} />
                  </div>
                ) : filteredRooms.length === 0 ? (
                  <div className="text-center py-8 text-ide-textMuted">
                    <Users size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No active rooms found</p>
                    <p className="text-xs mt-1">Create one to get started!</p>
                  </div>
                ) : (
                  filteredRooms.map(room => (
                    <button
                      key={room.id}
                      onClick={() => handleJoinRoom(room)}
                      className="w-full p-3 bg-ide-bg hover:bg-ide-surfaceHover border border-ide-border rounded-lg transition-all text-left group"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-ide-text group-hover:text-white">
                          {room.name}
                        </span>
                        <span className="text-xs text-ide-textMuted flex items-center gap-1">
                          <Users size={12} />
                          {room.userCount}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {room.tags?.map(tag => {
                          const preset = PRESET_TAGS.find(t => t.value === tag)
                          return (
                            <span
                              key={tag}
                              className={`px-1.5 py-0.5 rounded text-[10px] ${preset?.color || 'bg-gray-500/20 text-gray-400'}`}
                            >
                              #{preset?.label || tag}
                            </span>
                          )
                        })}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Room Name */}
              <div>
                <label className="block text-sm font-medium text-ide-text mb-2">
                  Room Name
                </label>
                <input
                  type="text"
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                  placeholder="My awesome coding room"
                  className="w-full bg-ide-bg border border-ide-border rounded-lg px-3 py-2 text-ide-text placeholder-ide-textDim focus:outline-none focus:border-ide-accent focus:ring-1 focus:ring-ide-accent/30"
                  maxLength={50}
                />
              </div>
              
              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-ide-text mb-2">
                  Tags (select up to 3)
                </label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_TAGS.map(tag => (
                    <button
                      key={tag.value}
                      onClick={() => toggleTag(tag.value)}
                      disabled={!selectedTags.includes(tag.value) && selectedTags.length >= 3}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                        selectedTags.includes(tag.value)
                          ? tag.color + ' ring-2 ring-current'
                          : selectedTags.length >= 3
                            ? 'bg-ide-bg text-ide-textDim cursor-not-allowed'
                            : 'bg-ide-bg text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover'
                      }`}
                    >
                      <Hash size={12} className="inline mr-1" />
                      {tag.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Create Button */}
              <button
                onClick={handleCreateRoom}
                disabled={!roomName.trim() || creating}
                className="w-full py-3 bg-ide-accent hover:bg-ide-accentHover text-white font-medium rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {creating ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus size={18} />
                    Create Room
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
